import gql from 'graphql-tag';
import React from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

import {FolderNameQuery, FolderNameQueryVariables} from './__generated/FolderNameQuery';
import {ProjectNameQuery, ProjectNameQueryVariables} from './__generated/ProjectNameQuery';

export const PROJECT_NAME_QUERY = gql`
  query ProjectNameQuery($projectId: StrictString!) @engComponent(name: ANALYSIS_TOOL) @skipAudit {
    project(id: $projectId) {
      ... on ProjectNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const FOLDER_NAME_QUERY = gql`
  query FolderNameQuery($folderId: StrictString!) @engComponent(name: ANALYSIS_TOOL) @skipAudit {
    folder(id: $folderId) {
      ... on FolderNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface DataSharingWarningBannerProjectProps {
  projectId: string | null;
}
interface DataSharingWarningBannerFolderProps {
  folderId: string | null;
}

export type DataSharingWarningBannerProps =
  | DataSharingWarningBannerProjectProps
  | DataSharingWarningBannerFolderProps;

function getMessage(resourceType: string, resourceName: string): string {
  return `Data in this analysis, including those pulled from other ${resourceType}, will be available to all users with access to ${resourceName}.`;
}

function DataSharingWarningProjectBanner(props: DataSharingWarningBannerProjectProps): JSX.Element {
  const result = useSkippableBenchlingQuery<ProjectNameQuery, ProjectNameQueryVariables>(PROJECT_NAME_QUERY, {
    variables: {projectId: props.projectId!},
    skip: props.projectId == null,
  });
  const resourceType = 'projects';
  const resourceName =
    result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.project)
      ? result.data.project.name
      : 'the selected project';

  return <InlineBanner bannerType={InlineBannerType.INFO} text={getMessage(resourceType, resourceName)} />;
}

function DataSharingWarningFolderBanner(props: DataSharingWarningBannerFolderProps): JSX.Element {
  const result = useSkippableBenchlingQuery<FolderNameQuery, FolderNameQueryVariables>(FOLDER_NAME_QUERY, {
    variables: {folderId: props.folderId!},
    skip: props.folderId == null,
  });
  const resourceType = 'folders';
  const resourceName =
    result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.folder)
      ? result.data.folder.name
      : 'the selected folder';

  return <InlineBanner bannerType={InlineBannerType.INFO} text={getMessage(resourceType, resourceName)} />;
}

export default function DataSharingWarningBanner(props: DataSharingWarningBannerProps): JSX.Element {
  if ('projectId' in props) {
    return <DataSharingWarningProjectBanner projectId={props.projectId} />;
  }
  return <DataSharingWarningFolderBanner folderId={props.folderId} />;
}
